import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import LayoutNavSectionCombinationListList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationListList.jsx';
import { setInCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import { ProgressProvider } from 'components/layout_and_quote_builder/utils/ProgressContext.jsx';
import { DebugPanel } from 'components/layout_and_quote_builder/DebugPopup.jsx';
// The listing's loading state is a modal, blocking popup -- a full-screen card with a short "Loading…" title and
// a determinate percentage bar while the manufacturing lines are scored. It reads more clearly than an inline
// bar. ListingProgressOverlay is Frank's own (it reuses the legacy overlay's styles but with the shorter title),
// so the legacy page's ProgressBarOverlay stays exactly as it is. The solve keeps Frank's own in-place skeleton
// (SolutionCardsSkeleton), whose single request has no percentage to show.
import ListingProgressOverlay from 'components/frank/ListingProgressOverlay.jsx';
import SolutionCardsSkeleton from 'components/frank/SolutionCardsSkeleton.jsx';
import SelectedSolutionSections from 'components/frank/SelectedSolutionSections.jsx';
// The shared de-select: it posts to the reset endpoint, clears the section server-side and tells the linked
// sections. A JS helper, not a Ruby service -- the same reuse the legacy cards get.
import resetSelectedListHandler from 'components/layout_and_quote_builder/quote_builder/resetSelectedListHandler.js';
import skeletonStyles from 'components/frank/_style.module.scss';
import { defaultRequestQueueManager } from 'components/layout_and_quote_builder/utils/RequestQueueManager.js';
import {
  readSelectedManufacturingLineList,
  writeSelectedManufacturingLineList,
  clearSelectedManufacturingLineList,
} from 'components/frank/utils/manufacturingLineListUrlState.js';

// The stable address of ONE distinct manufacturing line: (sub_section_index, identifier). The same line is
// reused across many lists, so its best score is fetched once under this key. See #bestScoreByLine.
const lineKey = (line) => `${line.sub_section_index}:${line.identifier}`;

// The star rating for a score: BEST (lowest) = 5 stars, worst = 1, by the score's position in the RANGE of the
// scores on screen -- the same shape the solution listing uses (sortSolutionsByScoreAndDrawStars). null for a
// plan with no score; 5 when there is only one score or they are all equal (all equally best).
const starRatingForScore = (score, minScore, maxScore) => {
  if (score == null) { return null; }

  const range = maxScore - minScore;
  if (!(range > 0)) { return 5; }

  const normalized = (score - minScore) / range;
  return Math.round(Math.max(1, Math.min(5, 5 - (normalized * 4))) * 10) / 10; // 5 down to 1, never 0
};

// A star row for a rating -- full stars, a half star, empty stars, then the numeric rating. The same rendering
// the legacy solution listing draws, in React.
const Stars = ({ rating }) => {
  if (rating == null) { return null; }

  const full = Math.floor(rating);
  const remainder = rating - full;
  const hasHalf = remainder >= 0.25 && remainder < 0.75;
  const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

  return (
    <span style={{ color: '#ffc107', fontSize: '14px' }}>
      {'★'.repeat(full)}
      {hasHalf ? (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <span style={{ color: '#ddd' }}>★</span>
          <span style={{ position: 'absolute', left: 0, width: '50%', overflow: 'hidden', color: '#ffc107' }}>★</span>
        </span>
      ) : null}
      {'☆'.repeat(empty)}
      <span style={{ marginLeft: '4px', color: '#666', fontSize: '13px' }}>{rating.toFixed(1)}</span>
    </span>
  );
};

// How many of each part's solutions survive into the combinations. Copied from the legacy listing: without a
// cap, a plan whose two parts have 7 solutions each would list 49 cards, and the 40th-best pairing is not a
// thing anyone reads.
const TOP_N_PER_PART = window.topNPerPart || 3;

// The area-ratio cap (a PERCENT: "20" == a printing bed up to 20% larger in area than the sheet is kept) that
// the /mcc Debug input starts on. Seeded from the server so it matches the applied default
// (MachineToSheetAreaRatioFilter::MAXIMUM_AREA_RATIO's percent form) with no second hardcoded value to drift;
// the literal fallback is only for surfaces that do not set the global, e.g. JS tests.
const DEFAULT_AREA_RATIO_PERCENT = window.frank_machine_area_ratio_percent ?? 20;

// Every way to pick one item from each array. Copied from the legacy listing's cartesianProduct.
const cartesianProduct = (arrays) => arrays.reduce((accumulator, currentArray) => {
  const result = [];
  accumulator.forEach((existingCombination) => {
    currentArray.forEach((item) => { result.push([...existingCombination, item]); });
  });
  return result;
}, [[]]);

// BEST FIRST, AND HOW MANY STARS -- copied from the legacy listing's own sortDivs
// (LayoutNavSectionCombinationList), so a solution ranks and reads identically on both pages.
//
// The stars are NOT a property of one solution: they are its score's position in the RANGE of the scores on
// screen (best = 5, worst = 1), so they can only be worked out once every card has answered. The legacy does it
// in the DOM rather than in React state for exactly that reason -- each card fetches its own score, and
// data-score appears on the grid item as each lands. Frank keeps the same shape, and takes only the two halves
// that are Frank's: the SORT and the STARS. The legacy's other two (the missing-machine button, the show-more
// limit) are its listing's own controls, which Frank does not have.
//
// LOWER IS BETTER: the score is a cost.
const sortSolutionsByScoreAndDrawStars = (grid) => {
  if (!grid) { return; }

  const items = Array.from(grid.children);
  if (items.length === 0) { return; }

  const scoreOf = (item) => parseInt(item.getAttribute('data-score'), 10);

  const sorted = [...items].sort((a, b) => scoreOf(a) - scoreOf(b));
  const alreadySorted = sorted.every((item, index) => item === items[index]);
  if (!alreadySorted) {
    sorted.forEach((item) => grid.appendChild(item));
  }

  // A score of 0 is "not answered yet", not "free" -- the legacy filters those out of the range, or an
  // unanswered card would clamp to a spurious 5.0 and sort to the front.
  const scores = items.map(scoreOf).filter((score) => !Number.isNaN(score) && score > 0);
  if (scores.length === 0) { return; }

  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const range = maxScore - minScore;
  if (range <= 0) { return; }

  items.forEach((item) => {
    const score = scoreOf(item);
    if (Number.isNaN(score)) { return; }

    const normalizedScore = (score - minScore) / range;
    const starRating = 5 - (normalizedScore * 4); // 5 down to 1, never 0
    const rounded = Math.round(Math.max(1, Math.min(5, starRating)) * 10) / 10;

    let container = item.querySelector('.stars-rating-container');
    if (!container) {
      container = document.createElement('span');
      container.className = 'stars-rating-container';
      item.prepend(container);
    }

    const fullStars = Math.floor(rounded);
    const remainder = rounded - fullStars;
    const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHtml = '<span style="color: #ffc107; font-size: 14px; margin-left: 8px;">';
    starsHtml += '★'.repeat(fullStars);
    if (hasHalfStar) {
      starsHtml += '<span style="position: relative; display: inline-block;">'
        + '<span style="color: #ddd;">★</span>'
        + '<span style="position: absolute; left: 0; width: 50%; overflow: hidden; color: #ffc107;">★</span>'
        + '</span>';
    }
    starsHtml += '☆'.repeat(Math.max(0, emptyStars));
    starsHtml += ` <span style="margin-left: 4px; color: #666; font-size: 13px;">${rounded.toFixed(1)}</span></span>`;

    container.innerHTML = starsHtml;
  });
};

// The label each filter's toggle shows, by the key the server sends. Reuses the legacy listing's own label
// keys, so a filter reads the same on both pages.
const FILTER_LABEL_KEYS = {
  smallest_die_cutting_machine_of_each_type: 'smallest_die_cutting_filter',
};

// Each machine-type filter's row heading, and how to label one of its ticks -- both the legacy listing's own
// keys, so the rows read identically on both pages.
const MACHINE_TYPE_FILTER_LABELS = {
  printing_machine_types: {
    headingKey: 'quote_builder.printing_machines',
    optionKey: (type) => `quote_builder.${type}`,
  },
  die_cutting_machine_types: {
    headingKey: 'quote_builder.die_cutting_machines',
    optionKey: (type) => `quote_builder.die_cutting_type_${type}`,
  },
};

// Frank lists a section's SubSectionManufacturingLines on /mcc, below the "Chosen material dimensions": per
// sub_section, the manufacturing lines that can make it, rendered through the SAME cards the legacy page uses
// (ManufacturingLineCard) -- so a manufacturing line reads identically on both pages.
//
// It lists SUB_SECTION manufacturing lines, not ManufacturingLineLists: a ManufacturingLineList is one
// complete way to manufacture the WHOLE section, which for overlapping sub_sections (Cover, Tray, and the
// combined "Tray & Cover" all describing the same parts) is an exact-cover across them. That comes later.
//
// What Frank does not have yet, and so does not render:
//   - "Material dimensions:" chips -- a Frank ManufacturingLine carries its members, not the material sheets
//     (the sheets are the finder's fit input). The card drops that block on its own when the key is absent.
//   - "Priced solutions" -- no Solution Finder yet, so the cards are told there is no count to wait for
//     (hidePricedSolutionCount) rather than being left dimmed behind a spinner forever.
//   - selection -- this is a read-only verify view while Frank grows, so the cards are not clickable.
//
// Admin-gated like the material-sheets line above it.
// The solution this section is already priced from, if any -- read back from the sections the page was
// rendered with (user_quote_nav_sections[].selected_lists), so a refresh or a shared link reopens on the
// priced section rather than on the solution list. It is stored per section, which is why it is looked up by
// sort_order rather than by position.
const restoreSelectedSolutions = (navSectionIndex) => {
  const sections = window.user_quote_nav_sections || [];
  const section = sections.find((candidate) => candidate.sort_order === navSectionIndex);
  const stored = section && section.selected_lists;

  return stored && stored.length > 0 ? stored : null;
};

// IS A SOLUTION CHOSEN? Asked of the LENGTH, never of truthiness, because the reset path sets the selection to
// an EMPTY ARRAY rather than to null -- `resetSelectedListHandler` does `setSelectedList(() => [])`, and the
// legacy section's own Back button (rendered inside QuoteBuilderNavSection, which is handed this setter) goes
// through it. `[]` is truthy in JavaScript, so a truthiness check kept the priced view on screen with nothing
// in it: the reset had already reached the server, and the page showed one orphaned button.
const hasSelection = (selectedSolutions) => Array.isArray(selectedSolutions) && selectedSolutions.length > 0;

const ManufacturingLineLists = ({ navSectionIndex, bobbinEnabled, config }) => {
  const [listing, setListing] = useState(null);
  // The reductions switched OFF for the SECTION: ['smallest_die_cutting_machine_of_each_type'].
  // Every one is ON by default -- switching one off is an administrator asking to SEE what it hides, so the
  // reduction is never a silent one.
  const [disabledFilters, setDisabledFilters] = useState([]);
  // The machine types ticked for the SECTION, by filter key: { printing_machine_types: ['offset'] }.
  // Absent means "not spoken yet" -- the server applies the filter's own default (Offset for printing;
  // everything but Digital for die-cutting), so the defaults live in ONE place rather than being duplicated here.
  const [selectedTypes, setSelectedTypes] = useState({});
  // The area-ratio cap the admin is experimenting with, as a percent. areaRatioPercent is the COMMITTED value
  // that drives the fetches (the listing, the solve and the scoring all reduce against it, so a plan is always
  // listed and solved against the same cap); areaRatioInput is the text shown while typing, committed on blur /
  // Enter -- not per keystroke, because each change re-runs a full listing and its heavy per-line scoring.
  const [areaRatioPercent, setAreaRatioPercent] = useState(DEFAULT_AREA_RATIO_PERCENT);
  const [areaRatioInput, setAreaRatioInput] = useState(String(DEFAULT_AREA_RATIO_PERCENT));
  // The ManufacturingLineList the viewer picked, by identifier -- the plan whose solutions are shown. null = the
  // listing, nothing picked.
  // Restored from the address bar up front (manufacturing_line_<index>) so a refresh / shared link reopens the
  // picked plan straight away, before the listing has even loaded. A stale value (a plan the listing that lands
  // does not hold) is dropped once the listing is known -- see the stale-restore guard below.
  const [pickedList, setPickedList] = useState(() => readSelectedManufacturingLineList(navSectionIndex));
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  // The picked plan's SOLUTION GROUPS: each is an ARRAY holding ONE solution per part, and is drawn as ONE
  // card. See #buildSolutionGroups.
  const [solutionGroups, setSolutionGroups] = useState(null);
  // The solutions grid, so the ranking pass can reach the cards' data-score as each lands.
  const solutionsGridRef = useRef(null);
  // The BEST (lowest) score of each DISTINCT manufacturing line, keyed by lineKey -- what the cards sort and
  // label by. Empty until all the per-line best-score requests have landed (the progress bar covers the wait).
  // null for a line that produces no solution (its list sorts last).
  const [bestScoreByLine, setBestScoreByLine] = useState({});
  // THE CHOSEN SOLUTION -- an ARRAY holding one solution per part (a solution GROUP, which is what one card
  // is), or null while none is chosen. Choosing one is what moves the section from "how would this be made" to
  // "what does it cost": it is stored on the section and the cost sections are priced from it.
  //
  // Held WITH THE SECTION IT BELONGS TO, and read back only for that section. The page switches section
  // WITHOUT remounting this component, and a child's effect runs before its parent's -- so a bare array would
  // be visible to children for one render under the NEW index while still holding the PREVIOUS section's
  // choice, and SelectedSolutionSections would post it there. That is not a wasted request: the endpoint
  // writes `lists` straight over the section's selected_lists, so section 0's solution would be saved onto
  // section 1 while the page, one effect later, correctly shows section 1 as unpriced.
  //
  // Restored from what the page was rendered with, so a refresh reopens on the priced section.
  const [selection, setSelection] = useState(() => ({
    index: navSectionIndex,
    solutions: restoreSelectedSolutions(navSectionIndex)
  }));

  const selectedSolutions = selection.index === navSectionIndex ? selection.solutions : null;

  // The setter the children are handed. They pass an array or an updater, exactly as they would to a plain
  // useState setter (the shared resetSelectedListHandler passes `() => []`); filing it against the section on
  // screen is this component's business, not theirs.
  const setSelectedSolutions = useCallback((value) => {
    setSelection((previous) => {
      const current = previous.index === navSectionIndex ? previous.solutions : null;
      return {
        index: navSectionIndex,
        solutions: typeof value === 'function' ? value(current) : value
      };
    });
  }, [navSectionIndex]);

  // THE PLAN THE CHOSEN SOLUTION WAS MADE ON, derived from the chosen CARDS rather than read from `pickedList`.
  // A list's identifier is exactly its manufacturing lines' identifiers in cover order
  // (Frank::ManufacturingLineList#identifier) and every card carries the line it was solved on, so the plan is
  // a property of the selection itself. It has to be: `pickedList` is page state, and the two ways a priced
  // section reaches the screen without one are both normal -- a link straight into a priced section never
  // picked anything, and switching section clears the pick while the selection is restored from the server.
  //
  // ALL OR NOTHING: a card with no identifier (a selection made on the legacy page, which stamps none) makes
  // the whole thing null. Dropping the empty ones instead would turn "I know some of the lines" into a
  // complete-looking identifier -- and on a two-part section that shortened string can MATCH a different,
  // single-line plan in the listing, which would then be drawn as the plan and scoped the scoring.
  const pricedLineIdentifiers = hasSelection(selectedSolutions)
    ? selectedSolutions.map((solution) => solution.diecutting_result.manufacturing_line_identifier)
    : [];
  const pricedListIdentifier = pricedLineIdentifiers.length > 0 && pricedLineIdentifiers.every(Boolean)
    ? pricedLineIdentifiers.join('|')
    : null;

  // THE ONE PLAN THE PAGE IS ABOUT, if any: the picked one while a solution is being chosen, the priced one
  // once it has been. Everything scoped to "the plan on screen" -- which lines get scored, which lists the star
  // ratings normalize against, which card is drawn -- follows this rather than the pick alone. Scoring did not,
  // which is why a priced section opened from a link scored every plan in the listing for a listing it never
  // shows.
  //
  // Once a solution is CHOSEN it is the derived plan or nothing -- never `pickedList`. The pick is whatever
  // manufacturing_line_<index> happens to say in the address bar, which on a deep link into a section priced
  // from a legacy selection is a plan with nothing to do with the chosen solution.
  const planOnScreen = hasSelection(selectedSolutions) ? pricedListIdentifier : pickedList;

  // THE SOLUTION GROUPS -- what a card actually is.
  //
  // A ManufacturingLineList makes the WHOLE section, and it can do that with more than one manufacturing line:
  // a shirt box's plan is "Kapak on the KBA + BOBST, Kutu on the 70x100 Pedal". Those two are not two answers,
  // they are two HALVES of one answer -- so one card must show a solution for EACH part, and the card is the
  // pair. Listing each part's solutions on their own says "here are some covers and some trays" and leaves the
  // reader to do the pairing.
  //
  // So the groups are the CARTESIAN PRODUCT across the plan's parts, copied from the legacy listing's
  // handlePartsMode:
  //     one part  -> [[s1], [s2], ...]                 (a group of one; every solution is already whole)
  //     two parts -> [[kapak1, kutu1], [kapak1, kutu2], ...]
  //
  // The product is taken over the BEST TOP_N_PER_PART of each part. Best needs the score -- which now arrives
  // WITH the solution (score_data, computed server-side by Frank::SolutionScore), so this is a plain
  // SYNCHRONOUS sort, no per-solution round trip.
  //
  // WARMING THE CACHE (not an XHR): the reused legacy solution card reads its score from the shared score cache
  // (LayoutNavSectionCombination#getScore checks the cache before fetching). Priming that cache from the
  // embedded score_data is what lets Frank reuse the legacy card WITHOUT its per-card fetch to
  // /layouts-nav-section-combination-score -- the card finds every score already answered.
  //
  // A part with NO solutions collapses the whole product to nothing, correctly: if the Kutu cannot be made on
  // its assigned machine, the PLAN cannot make the section, however many ways the Kapak has.
  const buildSolutionGroups = (data) => {
    const groups = (data && data.solutions) || [];

    // Prime the score cache from the embedded score_data, so the legacy card renders every solution with no XHR.
    groups.forEach((group) => group.solutions.forEach((solution) => {
      if (solution.score_data) { setInCache(solution.diecutting_result.uuid, solution.score_data); }
    }));

    const perPart = groups.map((group) => group.solutions).filter((list) => list.length > 0);

    // Every part must be makeable, or the plan makes nothing.
    if (perPart.length === 0 || perPart.length !== groups.length) { return []; }

    // One part: each solution is already a whole way to make the section.
    if (perPart.length === 1) { return perPart[0].map((solution) => [solution]); }

    // lower is better: the score is a cost. A missing score sorts last.
    const scoreOf = (solution) => (solution.score_data ? solution.score_data.score : Infinity);
    const bestPerPart = perPart.map((list) => [...list]
      .sort((a, b) => scoreOf(a) - scoreOf(b))
      .slice(0, TOP_N_PER_PART));

    return cartesianProduct(bestPerPart);
  };

  const toggleFilter = (key) => setDisabledFilters((previous) => (
    previous.includes(key) ? previous.filter((each) => each !== key) : [...previous, key]
  ));

  // Tick or untick one machine type. The CURRENT selection comes from the server (it applied the defaults), so
  // the first tick starts from what is actually on screen rather than from an empty set.
  const toggleMachineType = (key, type, currentlySelected) => setSelectedTypes((previous) => {
    const selected = previous[key] || currentlySelected;
    const next = selected.includes(type) ? selected.filter((each) => each !== type) : [...selected, type];
    return { ...previous, [key]: next };
  });

  // Commit the typed area-ratio percent -- on blur or Enter, so the expensive re-list / re-score runs once the
  // admin has finished a value, not on every keystroke. A blank or non-numeric entry (or a negative, which is
  // not a "percent larger") is rejected: the field snaps back to the committed value and nothing re-fetches. A
  // valid value that has not changed does not re-fetch either -- React bails on the same state.
  const commitAreaRatio = () => {
    const parsed = Number(areaRatioInput);
    if (areaRatioInput.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setAreaRatioInput(String(areaRatioPercent));
      return;
    }
    setAreaRatioPercent(parsed);
    setAreaRatioInput(String(parsed));
  };

  // The bobbin checkbox changes the section's material sheets, and the manufacturing lines are found over
  // those sheets -- so re-fetch on toggle, or the lines shown would be the ones for the previous sheets. A
  // filter toggle re-fetches for the same reason: the reduction happens server-side, on the found lines.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined) { return; }

    const query = new URLSearchParams();
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      // A query string cannot carry an empty array, and "absent" already means "use the default" -- so an
      // empty selection is sent as one blank entry, which the server reads as "nothing ticked".
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The admin's area-ratio cap, so the listing is reduced against the same value the solve and scoring use.
    query.set('area_ratio_percent', areaRatioPercent);
    const queryString = query.toString();

    // superseded guards against out-of-order responses: a slow earlier fetch must not land its (stale) listing
    // after a later one -- which would then drive the best-score batch over sheets it was not listed with.
    let superseded = false;
    fetch(`/mcc/${window.user_quote_id}/manufacturing-line-lists/${navSectionIndex}${queryString ? `?${queryString}` : ''}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (data && !superseded) { setListing(data); } })
      .catch((error) => console.error('Error fetching manufacturing line lists:', error));

    return () => { superseded = true; };
  }, [navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes, areaRatioPercent]);

  // The picked plan's SOLUTIONS: how this plan would actually make the section. Fetched only once a plan is
  // picked, because the solve is the expensive half -- the listing above answers without it, which is why the
  // cards can appear long before any solution does.
  //
  // The same inputs the listing was fetched with are sent, so the plan is solved with the very sheets and
  // filters it was listed with: a plan identifier only means anything against a given listing.
  useEffect(() => {
    // No plan picked, or a solution already CHOSEN: either way there is nothing to solve. Reopening a priced
    // section still had its plan in the address bar, so it fired a full solve whose answer was then never
    // rendered -- frank.contract calls the solve the expensive half of the page.
    if (!window.frank_mode || navSectionIndex === undefined || !pickedList || hasSelection(selectedSolutions)) {
      setSolutionGroups(null);
      return;
    }

    const query = new URLSearchParams();
    query.set('manufacturing_line_list', pickedList);
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The same cap the listing used -- a plan is only meaningful against the listing it came from.
    query.set('area_ratio_percent', areaRatioPercent);

    let superseded = false;
    setSolutionsLoading(true);
    fetch(`/mcc/${window.user_quote_id}/solutions/${navSectionIndex}?${query.toString()}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => buildSolutionGroups(data))
      .then((groups) => {
        if (superseded) { return; }
        setSolutionGroups(groups);
        setSolutionsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching solutions:', error);
        if (superseded) { return; }
        setSolutionGroups([]);
        setSolutionsLoading(false);
      });

    return () => { superseded = true; };
    // selectedSolutions is a dependency because the guard above READS it: going back from a priced section has
    // to solve the plan again, and choosing one has to stop solving.
  }, [pickedList, navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes, areaRatioPercent, selectedSolutions]);

  // BEST SCORES -- so a card can sort best-first and show its score. Scoring is heavy (every solution of every
  // line, min per line, no pruning: the cheapest way is not guessable from the layout), so WHERE it runs matters:
  //   - LISTING on screen (no plan picked): score EVERY distinct line, ONCE per listing, so the cards can sort
  //     and star. Many small per-line requests through defaultRequestQueueManager -- each well under the 15s
  //     request timeout -- with ListingProgressOverlay's modal bar over completed/total.
  //   - ONE plan on screen -- picked, or the plan a chosen solution is priced from (a click, or a refresh /
  //     deep link straight into either view): score ONLY that plan's lines. That card needs just its OWN best
  //     score; scoring all ~42 lines there is what made a deep link slow and ran the full progress bar for
  //     nothing. The rest are scored if and when the user goes Back to the listing.
  // A line is fetched ONCE (it is reused across many lists); a list's best is the SUM of its lines' bests.
  //
  // `fullyScoredListingRef` remembers the listing object we have already scored in full, so neither picking a
  // plan from a scored listing nor going Back to it re-scores (the scores are kept) -- only a NEW listing (a
  // filter / section change) or a Back from a deep link (never fully scored) triggers the full pass.
  const fullyScoredListingRef = useRef(null);
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined || !listing) { return undefined; }
    if (fullyScoredListingRef.current === listing) { return undefined; }

    const lists = listing.manufacturing_line_lists || [];
    // NOT "the picked plan": it may equally be the plan a chosen solution is priced from, which is no pick at
    // all. What it always is, is the one plan on screen.
    const planOnScreenList = planOnScreen ? lists.find((list) => list.identifier === planOnScreen) : null;
    // An identifier the listing does not hold (a stale deep link) is dropped by the stale-restore guard; there
    // is nothing to score until it resolves to the listing or a real plan.
    if (planOnScreen && !planOnScreenList) { return undefined; }

    // One plan -> just its lines (merged in, keeping any we already have). Listing -> every line, a fresh
    // full score; on COMPLETION it marks this listing done (below) so picking / Back does not repeat it.
    const scoresWholeListing = !planOnScreenList;
    const scoredLists = planOnScreenList ? [planOnScreenList] : lists;

    const distinctLines = [];
    const seen = new Set();
    scoredLists.forEach((list) => (list.manufacturing_lines || []).forEach((line) => {
      if (line.sub_section_index == null) { return; }
      const key = lineKey(line);
      if (!seen.has(key)) { seen.add(key); distinctLines.push(line); }
    }));
    if (distinctLines.length === 0) { return undefined; }

    // The SAME filter/bobbin params the listing used, so each line is scored over the very sheets it was listed
    // with.
    const filterParams = new URLSearchParams();
    if (bobbinEnabled !== undefined) { filterParams.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => filterParams.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) { filterParams.append(`${key}[]`, ''); return; }
      types.forEach((type) => filterParams.append(`${key}[]`, type));
    });
    filterParams.set('area_ratio_percent', areaRatioPercent);

    let superseded = false;
    // Cancel a superseded batch's requests. An aborted signal makes an in-flight fetch reject AND makes a
    // still-QUEUED fetch reject immediately when the queue reaches it -- WITHOUT sending the request -- so a
    // rapid filter toggle does not pile N x 42 heavy solves on the server (each request runs a full solve).
    const controller = new AbortController();
    if (scoresWholeListing) { setBestScoreByLine({}); }
    defaultRequestQueueManager.reset();

    Promise.all(distinctLines.map((line) => defaultRequestQueueManager.enqueue(() => {
      const query = new URLSearchParams(filterParams);
      query.set('manufacturing_line', line.identifier);
      query.set('sub_section_index', line.sub_section_index);
      return fetch(`/mcc/${window.user_quote_id}/manufacturing-line-best-score/${navSectionIndex}?${query.toString()}`,
                   { signal: controller.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => ({ key: lineKey(line), best: data ? data.best_score : null }))
        .catch(() => ({ key: lineKey(line), best: null })) // aborts and network errors both land here
        // Written the MOMENT this one line answers, not once the whole batch has: that is what lets each card
        // fill in its own score as it arrives, instead of the listing sitting blank behind a full-screen bar
        // and then appearing all at once. A superseded batch writes nothing.
        //
        // The key is recorded even when the score is null (an unmanufacturable line), because the card has to
        // tell "answered, cannot be made" from "not answered yet" -- they look different: an em dash against a
        // still-loading placeholder.
        .then((result) => {
          if (!superseded) {
            setBestScoreByLine((previous) => ({ ...previous, [result.key]: result.best }));
          }
          return result;
        });
    }))).then(() => {
      if (superseded) { return; }
      // Mark the listing fully scored only NOW, once the batch actually landed. Recording it at the START would
      // strand the listing scoreless if a pick interrupted the batch: the aborted run leaves bestScoreByLine {},
      // and the next run would skip on `ref === listing` and never score anything.
      if (scoresWholeListing) { fullyScoredListingRef.current = listing; }
    });

    return () => { superseded = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `listing` is the join point (a filter / bobbin /
    // area-ratio / section change mints a new listing before this should re-run, and navSectionIndex is stable
    // per mounted instance), so only `listing` and `planOnScreen` trigger this -- not the filter values (the
    // area-ratio cap among them) the body reads.
  }, [listing, planOnScreen]);

  // A filter change re-lists, and the picked plan may no longer exist in the new listing -- so drop the
  // selection rather than show solutions for a plan that is no longer on screen. Clear the best scores in the
  // SAME pass, so the cards never show the previous listing's scores against the new listing for a render (the
  // best-score effect clears them too, but only after the render that first sees the new listing).
  //
  // Skip the very FIRST run: the picked plan is restored from the URL on mount (above), and this clear must not
  // wipe that restore -- only a later filter / section change should drop the selection.
  const selectionResetSkipRef = useRef(true);
  useEffect(() => {
    if (selectionResetSkipRef.current) { selectionResetSkipRef.current = false; return; }
    setPickedList(null);
    setBestScoreByLine({});
    // The chosen solution goes too: it was chosen out of a listing these filters have just replaced, so the
    // plan it was found on may no longer be offered at all.
    //
    // A SECTION change is different from a filter change, though: the new section may already be priced, and
    // its own selection has to come back. This component is NOT remounted when the page switches section --
    // FrankQuoteBuilderPage renders one section at a time and LayoutNavSectionList keys its child by the map
    // POSITION, which is always 0 for a one-element array -- so restoring only in the useState initializer left
    // an already-priced second section (a shirt box's Kapak) showing the solution listing instead.
    setSelectedSolutions(restoreSelectedSolutions(navSectionIndex));
  }, [navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes, areaRatioPercent]);

  // Mirror the picked plan into the address bar so a refresh / shared link reproduces it. The param is removed
  // when the selection goes away -- go back, or a filter change that drops it.
  useEffect(() => {
    if (navSectionIndex === undefined) { return; }
    if (pickedList) {
      writeSelectedManufacturingLineList(navSectionIndex, pickedList);
    } else {
      clearSelectedManufacturingLineList(navSectionIndex);
    }
  }, [pickedList, navSectionIndex]);

  // A plan restored from the URL may name one the current listing does not hold (a shared link after the
  // catalogue or filters changed). Once the listing lands, drop such a stale selection so the page shows the
  // listing rather than an empty picked view.
  useEffect(() => {
    if (!listing || !pickedList) { return; }
    const lists = listing.manufacturing_line_lists || [];
    if (!lists.some((list) => list.identifier === pickedList)) { setPickedList(null); }
  }, [listing]);

  // Rank the solutions once their scores land. Each card fetches its OWN score (the legacy card does this
  // itself), so the scores arrive one at a time and the ranking can only settle after the last one -- which is
  // why the legacy re-runs sortDivs on a 200ms timer rather than once. Frank does the same, but STOPS: it polls
  // only while the solutions are on screen, and the interval is cleared when the picked plan goes away.
  useEffect(() => {
    if (!pickedList) { return undefined; }

    const timer = setInterval(() => sortSolutionsByScoreAndDrawStars(solutionsGridRef.current), 200);
    return () => clearInterval(timer);
  }, [pickedList, solutionGroups]);

  const canSee = window.current_user_admin || window.quote_builder_v2_enabled;
  if (!canSee || !listing) { return null; }

  // What the page LISTS -- the ONLY thing it lists: every complete way to manufacture the section. The
  // sub_sections are the working behind these and are not listed: "Kapak" on its own makes no shirt box, it
  // makes a cover, and every one of its manufacturing lines already appears inside the lists that use it.
  const manufacturingLineLists = listing.manufacturing_line_lists || [];

  // ANSWERED, not "answered with a score": a line whose request came back unmanufacturable is recorded as null,
  // so presence of the KEY is what separates "we know" from "still waiting". Reading the value cannot tell them
  // apart, and the card draws them differently.
  const lineScored = (line) => Object.prototype.hasOwnProperty.call(bestScoreByLine, lineKey(line));
  const listScored = (list) => list.manufacturing_lines.every(lineScored);

  // The plans actually ON SCREEN: every one while the listing shows, just the one plan otherwise (picked, or
  // the plan a chosen solution is priced from). Scoring is scoped the same way (see the best-score effect), so
  // this is also the set that can be expected to answer -- asking whether the plans the viewer cannot see have
  // answered would leave that one card waiting forever.
  const listsOnScreen = planOnScreen
    ? manufacturingLineLists.filter((list) => list.identifier === planOnScreen)
    : manufacturingLineLists;

  // Whether every plan on screen has answered. Individual cards no longer wait for this -- each shows its own
  // score as it lands -- but the two things that are a property of the SET still do: the best-first sort, and
  // the star ratings, which are a score's position in the range on screen and so cannot be known until the
  // range is complete.
  const bestScoresReady = listsOnScreen.length > 0 && listsOnScreen.every(listScored);

  // A LIST's best = the SUM of its lines' bests (parts are made independently on their own lines). null when any
  // of its lines is unmanufacturable (no solution) -- that list cannot make the section, so it sorts last.
  const listBestScore = (list) => {
    const perLine = list.manufacturing_lines.map((line) => bestScoreByLine[lineKey(line)]);
    if (perLine.some((score) => score == null)) { return null; }

    return perLine.reduce((sum, score) => sum + score, 0);
  };

  // The range of the lists' best scores on screen -- the star ratings normalize against it (best = 5, worst =
  // 1). Unmanufacturable plans (null) are excluded from the range and get no stars.
  const listBestScores = bestScoresReady
    ? listsOnScreen.map(listBestScore).filter((score) => score != null)
    : [];
  const minListScore = listBestScores.length ? Math.min(...listBestScores) : 0;
  const maxListScore = listBestScores.length ? Math.max(...listBestScores) : 0;
  // The filters are the SECTION's: one set, applied to every sub_section the lists are built from.
  const machineTypeFilters = listing.machine_type_filters || [];
  const filters = listing.filters || [];

  // Largest machine sheet dimension across every manufacturing line the page draws -- the common scale that
  // makes the per-machine rectangles comparable to each other (a bigger machine renders bigger), including
  // across the two lines stacked in one list. The same scale the legacy listing computes.
  const allManufacturingLines = manufacturingLineLists.flatMap((list) => list.manufacturing_lines);
  const maxMachineDimensionMm = Math.max(1, ...allManufacturingLines.flatMap((manufacturingLine) => [
    manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
    manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
  ].map((value) => parseFloat(value) || 0)));

  // The SECTION's machine-type rows: "Printing Machines: [x] Offset [ ] Digital ...". Ticking a type re-fetches
  // and every sub_section below narrows to the machines of the ticked types -- asked once, not once per
  // sub_section.
  const renderMachineTypeFilters = () => (
    <>
      {machineTypeFilters.map((filter) => {
        const labels = MACHINE_TYPE_FILTER_LABELS[filter.key];
        if (!labels) { return null; }

        const currentlySelected = filter.options.filter((option) => option.selected).map((option) => option.value);

        return (
          <div
            key={filter.key}
            data-machine-type-filter={filter.key}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '4px' }}
          >
            <span style={{ fontSize: '12px', color: '#555', fontWeight: 'bold' }}>{t(labels.headingKey)}:</span>
            {filter.options.map((option) => (
              <label
                key={option.value}
                style={{ fontSize: '12px', color: '#888', fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <input
                  type="checkbox"
                  checked={option.selected}
                  data-machine-type={option.value}
                  onChange={() => toggleMachineType(filter.key, option.value, currentlySelected)}
                />
                {t(labels.optionKey(option.value))}
              </label>
            ))}
          </div>
        );
      })}
    </>
  );

  // The SECTION's reduction toggles. Each reduces every sub_section's manufacturing lines; unticking one
  // re-fetches and the manufacturing lines it was hiding reappear.
  const renderFilterToggles = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '6px' }}>
      {filters.map((filter) => (
        <label
          key={filter.key}
          style={{ fontSize: '12px', color: '#888', fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <input
            type="checkbox"
            checked={filter.enabled}
            data-filter-key={filter.key}
            onChange={() => toggleFilter(filter.key)}
          />
          {t(`quote_builder.${FILTER_LABEL_KEYS[filter.key]}`)}
        </label>
      ))}
    </div>
  );

  // The always-on area-ratio reduction's cap, as an adjustable PERCENT -- the Debug knob for tuning how much
  // bigger than the sheet a printing bed may be before the line is dropped. It reads as the rule itself: "Drop a
  // printing bed more than [20] % larger in area than the sheet." Committed on blur / Enter (commitAreaRatio),
  // which re-lists and re-scores against the new cap.
  const renderAreaRatioControl = () => (
    <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      Drop a printing bed more than
      <input
        type="text"
        inputMode="numeric"
        data-area-ratio-percent
        value={areaRatioInput}
        onChange={(event) => setAreaRatioInput(event.target.value)}
        onBlur={commitAreaRatio}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitAreaRatio();
          }
        }}
        style={{
          width: '56px', padding: '2px 6px', fontSize: '12px', textAlign: 'right',
          border: '1px solid #d1d5db', borderRadius: '4px'
        }}
      />
      % larger in area than the sheet
    </label>
  );

  // A machine is drawn ONCE per list card. The lines of a list routinely share a machine (one press running
  // both the Cover and the Tray), and drawing it per line reads as though the plan needs two of them. So walk
  // the lines in order and, for a machine already drawn above, leave its column EMPTY rather than repeat it:
  // the empty slot IS the signal -- an empty printing space on the lower line means it prints on the machine
  // shown above it.
  //
  // ONE rule for every part_type, never an `if carton`: an offset pair's press and die-cutter and a flexo
  // line's single combined machine are all just machines. One Set holds them all -- every machine is a
  // UserUnitPriceAttribute row (keyed printing_machine / die_cutting_machine / printing_and_die_cutting_machine),
  // so the ids are unique across the kinds and a press can never collide with a die-cutter.
  const columnVisibilityFor = (manufacturingLines) => {
    // machineId -> the INDEX of the manufacturing line that drew it. Which one drew it matters, not merely
    // that one did: a manufacturing line with no box left is badged onto the line that draws its machines, and
    // only that line can honestly say "this part runs on these machines".
    const drawnBy = new Map();

    return manufacturingLines.map((manufacturingLine, index) => {
      const machineIds = [
        manufacturingLine.printing_machine_id,
        manufacturingLine.die_cutting_machine_id,
        manufacturingLine.printing_and_die_cutting_machine_id
      ];
      const alreadyDrawn = (machineId) => machineId != null && drawnBy.has(machineId);
      const ownMachineIds = machineIds.filter((machineId) => machineId != null);

      // The manufacturing lines that drew this one's machines -- undefined for a machine nobody drew yet.
      const owners = new Set(ownMachineIds.map((machineId) => drawnBy.get(machineId)));
      // Droppable ONLY when every machine came from ONE earlier manufacturing line. That line's machines ARE
      // this one's, so it can carry the badge. Machines drawn by two DIFFERENT lines have no honest target
      // (the part runs on a pair no single row shows), and a line nobody drew from still has a box of its
      // own -- both keep their row. A machine-less line has no owners at all, so it keeps its row too.
      const drawnByOneManufacturingLine = owners.size === 1 && !owners.has(undefined);

      const visibility = {
        hidePrintingColumn: alreadyDrawn(manufacturingLine.printing_machine_id),
        hideDieCuttingColumn: alreadyDrawn(manufacturingLine.die_cutting_machine_id),
        hideCombinedColumn: alreadyDrawn(manufacturingLine.printing_and_die_cutting_machine_id),
        drawsNothing: drawnByOneManufacturingLine,
        // The manufacturing line whose badges this one joins, or null when it keeps its own row.
        badgeOntoIndex: drawnByOneManufacturingLine ? [...owners][0] : null
      };

      // FIRST drawer wins: it is the row the machine is actually drawn on.
      machineIds.forEach((machineId) => {
        if (machineId != null && !drawnBy.has(machineId)) { drawnBy.set(machineId, index); }
      });
      return visibility;
    });
  };

  // One ManufacturingLineList as a single card: a complete way to manufacture the section. A list covering two
  // sub_sections stacks BOTH its manufacturing lines in the one card -- so a card is the familiar machine card,
  // with more machines in it. Each line is badged with the sub_section it makes, since a card holding two
  // identical-looking machine pairs is otherwise unreadable.
  //
  // The card decides which columns each manufacturing line draws, because it is the only place that knows the
  // list's OTHER manufacturing lines -- a ManufacturingLineCard renders the one it is handed.
  //
  // A manufacturing line whose machines were ALL drawn by ONE line above has no box left, so it is not given a
  // row of its own: an empty row under a lone badge reads as unfinished. Its part is badged onto THAT line --
  // the one whose machines are its machines -- so the card reads "Kapak and Kutu, on this pair".
  // alone: the plan is the only card on screen (the picked view), so it is not one of a 4-across grid and must
  // not be squeezed to a quarter width.
  // readOnly: the priced view, where the card names the plan the prices are about rather than offering a pick.
  // Choosing again there goes through the "choose a different solution" button, which de-selects server-side
  // too -- a card that silently un-picked the plan under a priced section would leave the two disagreeing.
  const renderManufacturingLineList = (list, { alone = false, readOnly = false } = {}) => {
    const columnVisibility = columnVisibilityFor(list.manufacturing_lines);
    const drawnManufacturingLines = list.manufacturing_lines
      .map((manufacturingLine, index) => ({ manufacturingLine, index }))
      .filter(({ index }) => !columnVisibility[index].drawsNothing);

    // This manufacturing line's own part, plus the parts of any whose machines IT draws. Badging onto the
    // owner rather than the first row is what keeps the card honest once a list has more than two lines: with
    // three, the machines of the third can belong to the second, and saying it runs on the first's pair would
    // name machines it never touches.
    const sectionLabelsFor = (index) => [
      list.manufacturing_lines[index].sub_section_label,
      ...list.manufacturing_lines
        .filter((_manufacturingLine, otherIndex) => columnVisibility[otherIndex].badgeOntoIndex === index)
        .map((manufacturingLine) => manufacturingLine.sub_section_label)
    ];

    const picked = pickedList === list.identifier;
    // The priced view's card IS the plan in question, so it carries the picked highlight whether or not the
    // page still holds a pick -- a link straight into a priced section never had one.
    const highlighted = picked || readOnly;
    // The plan's best (cheapest) way to make the whole section -- the sum of its lines' bests. Shown once every
    // line's score has landed; '—' when the plan is unmanufacturable (a line with no solution). Its star rating
    // is the score's position in the range on screen: best = 5 stars, worst = 1 (linear min-max, matching the
    // solution cards).
    const scored = listScored(list);
    const listBest = scored ? listBestScore(list) : null;
    const rating = bestScoresReady ? starRatingForScore(listBest, minListScore, maxListScore) : null;

    return (
      <div
        key={list.identifier}
        className={styles.combinations_grid_item_cont}
        data-manufacturing-line-list={list.identifier}
        // Picking a plan asks "how would THIS plan make the section?" -- the solve, which the listing
        // deliberately does not do. Clicking the picked one again goes back to the listing.
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={readOnly ? undefined : () => setPickedList(picked ? null : list.identifier)}
        onKeyDown={readOnly ? undefined : (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setPickedList(picked ? null : list.identifier);
          }
        }}
        style={{
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
          flex: alone ? '0 0 auto' : '0 0 calc(25% - 16px)',
          maxWidth: alone ? '360px' : 'calc(25% - 16px)',
          margin: '8px', padding: '12px 8px', cursor: readOnly ? 'default' : 'pointer',
          border: highlighted ? '2px solid var(--brand, #eb5424)' : '1px solid transparent',
          borderRadius: '6px'
        }}
      >
        {/* THE SCORE SLOT, always the same height so a card never jumps when its number lands. Each row fills
            in as soon as what it needs is known: the VALUE the moment this plan's own lines have answered, the
            STARS only once every plan on screen has, because a rating is a position in that range. A plan that
            answered "cannot be made" shows an em dash -- it is answered, not still loading. */}
        <div
          data-best-score={listBest == null ? '' : listBest}
          style={{ textAlign: 'center', marginBottom: '6px' }}
        >
          <div style={{
            marginBottom: '2px', minHeight: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          >
            {bestScoresReady
              ? (rating != null ? <Stars rating={rating} /> : null)
              : <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonStars}`} />}
          </div>
          {scored ? (
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: listBest == null ? '#c00' : '#2e7d32' }}>
              {listBest == null ? '—' : `Skor: ${listBest.toLocaleString()}`}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonValue}`} />
            </div>
          )}
        </div>
        {drawnManufacturingLines.map(({ manufacturingLine, index }) => (
          <ManufacturingLineCard
            key={`${manufacturingLine.identifier}-${index}`}
            manufacturingLine={manufacturingLine}
            maxMachineDimensionMm={maxMachineDimensionMm}
            sectionLabels={sectionLabelsFor(index)}
            bare
            hidePricedSolutionCount
            hidePrintingColumn={columnVisibility[index].hidePrintingColumn}
            hideDieCuttingColumn={columnVisibility[index].hideDieCuttingColumn}
            hideCombinedColumn={columnVisibility[index].hideCombinedColumn}
            widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
          />
        ))}
      </div>
    );
  };

  // The control that goes back to the listing. The picked plan itself stays on screen as its own card above the
  // solutions, so this is only the way back.
  const renderGoBack = () => (
    <button
      type="button"
      data-frank-go-back
      onClick={() => setPickedList(null)}
      className="btn simple-btn"
      style={{ marginBottom: '12px' }}
    >
      Go Back
    </button>
  );

  // The picked plan's SOLUTION GROUPS -- one CARD per group, each holding one solution per part.
  //
  // A plan makes the WHOLE section, and may need two manufacturing lines to do it ("Kapak on the KBA + BOBST,
  // Kutu on the 70x100 Pedal"). Those are two halves of ONE answer, so one card carries both: the legacy's
  // LayoutNavSectionCombinationListList takes a `list` of solutions and draws them all inside a single grid
  // item, which is exactly what a group is (see #buildSolutionGroups for how they are combined).
  //
  // A plan with NO groups is a real answer, not a blank: its machines can be listed for the section's sheets
  // and still fold nothing both beds can run -- and if ONE part cannot be made, the plan cannot make the
  // section at all. So it says so, rather than showing an empty page that reads as broken.
  const renderSolutions = () => {
    // THE SOLVE, in place: the picked plan's card stays on screen above this and the grid area shows the work
    // instead of a full-screen card over the whole page. It draws a fixed few placeholders and an INDETERMINATE
    // bar -- the solve is one request, so neither how many cards are coming nor how far along it is can be
    // known (see SolutionCardsSkeleton).
    if (solutionsLoading) { return <SolutionCardsSkeleton />; }

    const groups = solutionGroups || [];

    return (
      <div data-frank-solutions={pickedList}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textAlign: 'center' }}>
          Solutions ({groups.length}) — how this plan makes the section
        </div>

        {groups.length === 0 ? (
          <div style={{ textAlign: 'center', fontSize: '12px', color: '#c00', padding: '8px' }}>
            This plan produces no solutions: one of its parts cannot be made on the machine it is assigned to.
          </div>
        ) : null}

        {/* THE LEGACY'S OWN SOLUTION VIEWS, reused verbatim -- the same reuse ManufacturingLineCard already
            gets, and the same distinction: legacy COMPONENTS are fine, legacy SERVICES are not
            (frank.contract, THE FRONT DOOR). A solution reads identically on both pages BY CONSTRUCTION rather
            than by a Frank copy that drifts from it.

            It works because Frank's Frank::DieCuttingSolution#to_hash is deliberately the legacy's shape, so
            the props map straight onto it: diecutting_result, nav_section_id, count, and
            material_layout_dimension as layout_dimension. That was the point of keeping the hash identical. */}
        <div className={styles.combinations_grid} ref={solutionsGridRef}>
          {groups.map((group) => (
            <LayoutNavSectionCombinationListList
              key={group.map((solution) => solution.diecutting_result.uuid).join('|')}
              list={group}
              count={group[0].count}
              setSelectedList={setSelectedSolutions}
            />
          ))}
        </div>
      </div>
    );
  };

  // THE LISTING: every complete way to manufacture the section. Shown only while nothing is picked. Sorted
  // best-first once every line's score has landed (listing order until then -- the progress overlay covers it).
  const renderListing = () => {
    const sortedLists = bestScoresReady
      ? [...manufacturingLineLists].sort((a, b) => {
        const scoreA = listBestScore(a);
        const scoreB = listBestScore(b);
        if (scoreA == null) { return scoreB == null ? 0 : 1; } // unmanufacturable plans sort last
        if (scoreB == null) { return -1; }
        return scoreA - scoreB; // lower score is better -- it is a cost
      })
      : manufacturingLineLists;

    return (
      <>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textAlign: 'center' }}>
          Manufacturing Line Lists ({manufacturingLineLists.length}) — every complete way to manufacture this
          section{bestScoresReady ? ', sorted by best score' : ''}. Click one to see its solutions.
        </div>
        {/* The listing's progress while its manufacturing lines are scored: the modal, blocking popup with a
            short "Loading…" title and a determinate percentage bar, over the whole page. It clears itself once
            every line has answered. */}
        <ListingProgressOverlay />
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
          {sortedLists.map((list) => renderManufacturingLineList(list))}
        </div>
      </>
    );
  };

  // THE PICKED VIEW: the plan on its own, centred, and its solutions under it -- the same shape the legacy page
  // uses (LayoutNavSectionCombinationList: Go Back, the selected card centred, an <hr />, then the solutions).
  //
  // It REPLACES the listing rather than appending to it. Appending is what the first cut did, and with 237 plan
  // cards above them the solutions landed several screens down: clicking a card looked like nothing happened,
  // even though the solve had run and the cards had rendered. A DOM assertion could not see that -- only opening
  // the page could.
  const renderPickedPlan = () => {
    const picked = manufacturingLineLists.find((list) => list.identifier === pickedList);

    return (
      <>
        {renderGoBack()}
        {picked ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderManufacturingLineList(picked, { alone: true })}
          </div>
        ) : null}
        <hr />
        {renderSolutions()}
      </>
    );
  };

  // THE CHOSEN SOLUTION, and the section priced from it. It REPLACES the listing and the solutions, the way the
  // legacy page does: once a solution is chosen the question is no longer which one, and leaving the whole
  // listing above the prices buries them (the same reason the picked plan replaces the listing).
  const renderSelectedSolution = () => {
    // The plan the section is priced from, drawn from the listing so it is the very card the viewer picked.
    // Absent when the listing no longer offers it -- a filter change since, or a selection made on the legacy
    // page, which stamps no manufacturing line on its cards. The prices are still about the chosen solution
    // then, so the plan card is simply left out rather than the whole block withheld.
    const pricedPlan = planOnScreen
      ? manufacturingLineLists.find((list) => list.identifier === planOnScreen)
      : null;

    return (
      <>
        <button
          type="button"
          data-frank-choose-another-solution
          // Through the SHARED reset handler, so the server forgets the selection too. Clearing only the page's
          // state left user_quote_nav_sections.selected_lists and the quote's builder_payload still holding the
          // rejected solution: a refresh reopened the section priced from it, and the Manage tab's totals went on
          // counting it. It also resets the linked sections and reloads when the server says to, which the legacy
          // section's own Back button has always done -- this page had a second button that did none of it.
          onClick={() => resetSelectedListHandler({
            index: navSectionIndex,
            setSelectedList: setSelectedSolutions
          })}
          style={{
            marginBottom: '12px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
            border: '1px solid var(--rule, #e3e7ec)', borderRadius: 'var(--dct-radius, 8px)',
            background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)'
          }}
        >
          ← {t('quote_builder.choose_a_different_solution')}
        </button>

        {/* WHAT THE PRICES ARE ABOUT, kept above them: the plan the section is manufactured on, and the solution
            card that was chosen off it -- the two cards the viewer clicked through to get here. Without them the
            priced view is a page of forms with no subject, and the only way to see which plan and which solution
            it was pricing was to go back, which throws the selection away.

            Both are the cards the rest of the page already draws, not a summary written again here: the plan
            through renderManufacturingLineList (the listing's own card), the solution through the legacy
            LayoutNavSectionCombinationListList (the same card the solutions grid shows). So they read identically
            to the ones that were clicked.

            READ-ONLY, both of them: choosing again is the button above, which de-selects on the server too. */}
        <div
          data-frank-priced-from
          style={{
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start',
            marginBottom: '12px'
          }}
        >
          {pricedPlan ? renderManufacturingLineList(pricedPlan, { alone: true, readOnly: true }) : null}
          <LayoutNavSectionCombinationListList
            // THE SELECTION ITSELF, deliberately not a copy. The shared card writes num_total_diecutting /
            // num_total_printing / num_of_drawings_in_set back into whatever `list` it is handed once its score
            // lands, and that write is LOAD-BEARING: SolutionDimensions in the table below reads those totals
            // off the solution, so a copy leaves both columns reading "(0)".
            //
            // What made that mutation dangerous was never the mutation -- it was that a stale selection could
            // be visible under the WRONG section index for one render, which turned the drift into a post
            // against another section. The selection is tagged with its section now, so it cannot be.
            list={selectedSolutions}
            count={selectedSolutions[0].count}
            // A no-op: the card is here to say WHICH solution is priced, not to choose one. The shared card
            // always wires its container to setSelectedList, so this is where the click is dropped.
            setSelectedList={() => {}}
          />
        </div>

        <SelectedSolutionSections
          config={config}
          navSectionIndex={navSectionIndex}
          selectedSolutions={selectedSolutions}
          setSelectedSolutions={setSelectedSolutions}
        />
      </>
    );
  };

  return (
    <ProgressProvider>
      <div style={{ marginTop: '10px', marginBottom: '10px', position: 'relative' }}>

      {/* Asked ONCE for the whole section, above the sub_sections they all narrow. Hidden once a plan is picked
          or a solution chosen: the filters narrow the LISTING, and the listing is not what is on screen then. */}
      {!pickedList && !hasSelection(selectedSolutions) ? (
        <div data-section-machine-filters style={{ marginBottom: '12px' }}>
          {renderMachineTypeFilters()}
          <DebugPanel title="Filters">{renderFilterToggles()}</DebugPanel>
          <DebugPanel title="Area ratio">{renderAreaRatioControl()}</DebugPanel>
        </div>
      ) : null}

      {(() => {
        if (hasSelection(selectedSolutions)) { return renderSelectedSolution(); }

        return pickedList ? renderPickedPlan() : renderListing();
      })()}

      {/* The sub_sections themselves are NOT listed: each is the working behind the lists, not an answer --
          "Kapak" on its own makes no shirt box, it makes a cover. Every one of its manufacturing lines already
          appears above, inside the lists that use it. */}
      </div>
    </ProgressProvider>
  );
};

export default ManufacturingLineLists;
